import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/ep01_script.md ("The Mystery Fatigue").

export const methylationMaya: Scenario = {
  slug: 'methylation-maya',
  series: 'Methylation & MTHFR',
  title: 'The mystery fatigue her "normal" blood test missed',
  socialHook:
    "Maya, 38: exhausted, flat, and tingling for a year while every test looked normal. The clue was not in how much B12 she had — it was in how much her cells could use.",
  meta: {
    description:
      "A fictional case study: Maya's standard labs looked normal, but her symptoms kept pointing to B12 and folate. Her MTHFR and FUT2 results explain why the issue was use and absorption, not effort.",
  },

  persona: {
    name: 'Maya',
    age: 38,
    sex: 'Female',
    ancestry: 'West African',
    occupation: 'Knowledge worker',
    chiefConcern:
      "A year of exhaustion, low mood, and buzzing in her hands and feet, even though her blood work keeps being called normal.",
    familyHistory:
      "Mum has always been tired. Dad had a heart attack at 58; his doctor flagged unusually high levels of a compound called homocysteine.",
  },

  clinicalPicture: {
    symptoms: [
      "Exhausted every day — wakes up tired even after a full night's sleep",
      'Can barely think by 2pm',
      "Feels flat and unmotivated — \"like the volume's been turned down on everything\"",
      'Buzzing or tingling in her fingertips and the soles of her feet — even at night',
    ],
    labs: [
      { name: 'Vitamin B12', value: '215 pg/mL', refRange: '200–900 pg/mL', flag: 'normal' },
      { name: 'Folate', value: '6.2 ng/mL', refRange: '4.6–18.7 ng/mL', flag: 'normal' },
    ],
    medications: ['None'],
    supplements: ['A standard B-complex from the pharmacy — taken occasionally'],
    lifestyle: ['Switched to a mostly plant-based diet about 18 months ago'],
  },

  genetics: [
    {
      gene: 'MTHFR',
      variant: 'C677T',
      rsid: 'rs1801133',
      genotype: 'C/T (one copy)',
      plainMeaning:
        "Maya can have folate in her blood but still struggle to turn enough of it into the active form her cells need.",
      technicalDetail:
        'MTHFR C677T reduces the activity of the methylenetetrahydrofolate reductase enzyme, which converts dietary folate to 5-methyltetrahydrofolate — the form needed for the methylation cycle. One copy (heterozygous) reduces enzyme activity by roughly 35%; the effect compounds with other MTHFR variants.',
    },
    {
      gene: 'MTHFR',
      variant: 'A1298C',
      rsid: 'rs1801131',
      genotype: 'A/C (one copy)',
      plainMeaning:
        "This second result makes the same folate-processing step slower again, which helps explain why a normal folate number may not feel normal in her body.",
      technicalDetail:
        'MTHFR A1298C primarily affects the BH4 cofactor arm of the MTHFR enzyme. When compound heterozygous with C677T, combined enzyme activity can fall to 25–30% of normal — significantly greater impairment than either variant alone. This double hit is a recognized cause of functional folate insufficiency even when serum folate is within range.',
    },
    {
      gene: 'FUT2',
      variant: 'Non-secretor',
      rsid: 'rs601338',
      genotype: 'A/A',
      plainMeaning:
        "Maya may take in B12, but her gut is less efficient at getting it into her system.",
      technicalDetail:
        'FUT2 encodes the enzyme responsible for secreting ABO blood group antigens into mucosal surfaces. Non-secretor status (homozygous A/A at rs601338) is associated with reduced intrinsic factor expression and altered gut microbiome composition, both of which impair passive and active absorption of cobalamin. Non-secretors have ~30% lower serum B12 on average compared to secretors with equivalent dietary intake.',
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'A year of feeling unwell on paper',
      body: "Maya is 38, generally healthy, and takes no medications. For a year she has woken up tired, lost her usual spark, and felt a strange buzzing in her fingertips and the soles of her feet. Her family doctor ran the usual blood work. Everything landed inside the reference range. On paper she was fine. In real life, she was not.",
    },
    {
      type: 'narrative',
      heading: 'The numbers that hid the clue',
      body: "Her B12 and folate were both technically normal, so nothing was flagged. But a standard blood test shows what is circulating in the blood. It does not prove that enough B12 and folate are getting absorbed, activated, and used inside cells. That is where the mystery starts to make sense.",
    },
    {
      type: 'narrative',
      heading: 'Two clues were already there',
      body: "Two details change the picture. She moved to a mostly plant-based diet 18 months ago, cutting back her main food source of B12 just before the symptoms began. Her dad's high homocysteine is another clue: that compound can build up when the same B12 and folate pathway is not running smoothly. The clues point in one direction.",
    },
    {
      type: 'reveal',
      heading: "Her blood looked fine. Her cells didn't.",
      body: "The issue was not whether Maya cared enough or ate the right foods. The issue was absorption and activation. Her FUT2 result helps explain why less B12 may reach her system, and her MTHFR results help explain why folate processing is slower once it gets there. Her B12 of 215 pg/mL sits inside the lab range, but that does not prove her cells have enough usable B12 and folate. This is how she can feel exhausted, flat, and tingly while the standard labs look fine.",
    },
    {
      type: 'reveal',
      heading: 'The supplement clue',
      body: "The supplement looked helpful, but it may not have matched her biology. Her pharmacy B-complex contains folic acid, a synthetic form of folate that people with MTHFR variants may convert less efficiently. For Maya, the better question is not \"more B vitamins?\" but \"which forms can my body actually use?\" Her dad's high homocysteine fits the same pathway, making this a family pattern worth discussing.",
    },
    {
      type: 'action',
      heading: 'What Maya brings to her clinician',
      items: [
        'Ask for homocysteine and MMA (methylmalonic acid) testing to see whether B12 and folate are working inside her cells, not just appearing in the blood.',
        'Ask whether methylcobalamin and methylfolate make sense for her, because these are active forms that bypass some of the conversion work.',
        'Pause the pharmacy B-complex until she has reviewed the ingredients and plan with her clinician.',
        'Track energy and tingling daily for two weeks so the follow-up visit includes a pattern, not just a feeling.',
      ],
    },
  ],

  // ── Widget beats ─────────────────────────────────────────────────────────────

  // Wellness goal check-in slider — 14 days, turning point at day 8 (new supplements start)
  // Appended after the action beat so the page shows the outcome, not just the advice.
  // (These are fictional illustrative data points.)
  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Maya's check-in: 14 days of tracking",
      goalLabel: 'Daily energy & tingling check-in',
      caption:
        "Maya logged energy and tingling every morning. The first week looked the same as always. After she switched to methylcobalamin on day 8, the pattern finally started to move.",
      days: [
        { day: 1,  scores: [{ label: 'Energy', value: 3 }, { label: 'Tingling', value: 4 }], note: "Exhausted before I even started. Tingling bad last night." },
        { day: 2,  scores: [{ label: 'Energy', value: 2 }, { label: 'Tingling', value: 4 }], note: "Worst day so far. Couldn't concentrate at all." },
        { day: 3,  scores: [{ label: 'Energy', value: 3 }, { label: 'Tingling', value: 3 }], note: "Slightly better but still crashing by lunch." },
        { day: 4,  scores: [{ label: 'Energy', value: 3 }, { label: 'Tingling', value: 4 }], note: "Tingling woke me up again at 3am." },
        { day: 5,  scores: [{ label: 'Energy', value: 4 }, { label: 'Tingling', value: 3 }], note: "A tiny bit more energy. Could be placebo." },
        { day: 6,  scores: [{ label: 'Energy', value: 3 }, { label: 'Tingling', value: 4 }], note: "Nope. Back to square one." },
        { day: 7,  scores: [{ label: 'Energy', value: 4 }, { label: 'Tingling', value: 3 }], note: "Family doctor appointment today. Finally have real data to show her." },
        { day: 8,  scores: [{ label: 'Energy', value: 4 }, { label: 'Tingling', value: 3 }], note: "Started methylcobalamin this morning.", milestone: "Started methylcobalamin" },
        { day: 9,  scores: [{ label: 'Energy', value: 4 }, { label: 'Tingling', value: 2 }], note: "Tingling a bit quieter today. Not sure if it's real yet." },
        { day: 10, scores: [{ label: 'Energy', value: 5 }, { label: 'Tingling', value: 2 }], note: "Made it to 4pm without the usual crash. First time in months." },
        { day: 11, scores: [{ label: 'Energy', value: 6 }, { label: 'Tingling', value: 2 }], note: "Mood noticeably better. Mum noticed too." },
        { day: 12, scores: [{ label: 'Energy', value: 6 }, { label: 'Tingling', value: 1 }], note: "Slept through the night — no tingling waking me up." },
        { day: 13, scores: [{ label: 'Energy', value: 7 }, { label: 'Tingling', value: 1 }], note: "Feels like myself again. First time I can say that in over a year." },
        { day: 14, scores: [{ label: 'Energy', value: 7 }, { label: 'Tingling', value: 1 }], note: "Following up with family doctor next week. The data tells the story." },
      ],
    },
    {
      type: 'heatmap' as const,
      heading: 'The week the pattern changed',
      caption: "Each square is one day. Darker red means worse. Green means a better day. Gray means not logged. Week 5 is when Maya switched supplements.",
      weekLabels: [
        'Week 1', 'Week 2', 'Week 3', 'Week 4',
        'Week 5 — new supplements', 'Week 6', 'Week 7', 'Week 8',
      ],
      rows: [
        {
          symptom: 'Energy',
          //        W1                    W2                    W3                    W4                    W5                    W6                    W7                    W8
          days: [4,4,3,4,3,4,3,  3,4,3,4,4,3,4,  4,3,4,3,3,4,3,  3,4,3,4,3,3,4,  3,2,2,2,2,2,1,  2,1,1,2,1,2,1,  1,1,2,1,1,1,2,  1,1,1,0,1,1,1],
        },
        {
          symptom: 'Tingling',
          days: [3,4,4,3,4,3,4,  4,3,4,4,3,4,3,  3,4,3,4,4,3,4,  4,3,4,3,4,4,3,  3,3,2,2,2,2,1,  2,1,2,1,1,2,1,  1,1,1,1,2,1,1,  1,1,0,1,1,1,1],
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'methylation-maya',
    label: 'Explore your own methylation in Coach',
  },
}
